import { get, off, onValue, ref, update } from "firebase/database";
import { db } from "../lib/firebase";
import { getPathDatabaseByRole } from "./auth";
import { LaunchError } from "../utils/errors";
import {
  isCreativeNotification,
  mapVisibleCreativeJobsToNotifications,
  normalizeCreativeJobs,
} from "../utils/creativeJobsNotifications";
import {
  clearCreativeJobProgress,
  migrateCreativeJobProgress,
} from "../utils/creativeJobProgressStorage";
import {
  promoteCreativeNotification,
  removeNotification,
  syncCreativeJobNotificationsAction,
} from "./alerts";

export { isCreativeNotification };

export const startListenerCreativeJobs =
  ({
    advertiserID,
    onUpdatedDataCallback = () => {},
    onReferenceAvailable = () => {},
  }) =>
  async (dispatch) => {
    try {
      if (!advertiserID) {
        onUpdatedDataCallback([]);
        return false;
      }

      const dbRef = ref(db, `jobs/${advertiserID}`);
      onReferenceAvailable(dbRef);

      onValue(dbRef, (snapshot) => {
        const jobs = normalizeCreativeJobs(snapshot.val());
        onUpdatedDataCallback(jobs);
      });

      return true;
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startListenerCreativeJobs",
          tags: { action: "creativeJobs" },
          level: "error",
        })
      );
      return false;
    }
  };

export const stopListenerCreativeJobs =
  ({ listenerRef } = {}) =>
  async () => {
    if (!listenerRef) return;
    await off(listenerRef);
  };

export const dismissCreativeJob =
  ({ advertiserID, jobID }) =>
  async (dispatch) => {
    try {
      if (!advertiserID || !jobID) return { ok: false };

      const jobRef = ref(db, `jobs/${advertiserID}/${jobID}`);
      const snapshot = await get(jobRef);

      if (!snapshot.exists()) {
        return { ok: true, skipped: true };
      }

      const dismissedAt = Date.now();
      const jobData = snapshot.val();

      await update(ref(db), {
        [`jobs/${advertiserID}/${jobID}`]: null,
        [`jobsDismissed/${advertiserID}/${jobID}`]: {
          ...jobData,
          dismissedAt,
          updateTime: dismissedAt,
        },
      });

      return { ok: true };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "dismissCreativeJob",
          tags: { action: "creativeJobs" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };

export const syncCreativeJobNotifications = (jobs) => (dispatch) => {
  const creativeJobNotifications = mapVisibleCreativeJobsToNotifications(jobs);
  dispatch(syncCreativeJobNotificationsAction(creativeJobNotifications));
};

export const promoteCreativeJobNotification =
  ({ fromId, jobID }) =>
  (dispatch) => {
    migrateCreativeJobProgress({ fromKey: fromId, toKey: jobID });
    dispatch(promoteCreativeNotification({ fromId, jobID }));
  };

export const closeCreativeNotification = (alertId) => (dispatch, getState) => {
  const notifications = getState()?.alerts?.notifications || [];
  const notification = notifications.find((item) => item.id === alertId);
  const storageKey = notification?.jobID ?? alertId;
  const jobID = notification?.jobID;

  clearCreativeJobProgress(storageKey);
  dispatch(removeNotification(alertId));

  if (!jobID) return;

  const { userID: advertiserID } = dispatch(getPathDatabaseByRole());
  dispatch(dismissCreativeJob({ advertiserID, jobID }));
};
